import type { TenantBrand } from '@tendedloop/shared';
import { bandColors } from '@tendedloop/shared/brand-colors';

/**
 * The building's mark on its band. Most brand logos are drawn for black (their
 * light and gold parts vanish on white); logos with dark artwork say
 * `logoBackground: 'light'` and get a white band. The logo only ever appears
 * inside this band. The splash sits on the band's lower edge, never behind text.
 *
 * `hero`: the top of the sign-in screen. `page`: the full-height band of the
 * signed-in screens (home, welcome, lease), the page's banner. `bar`: the slim
 * header of a registration step, so the form and its action stay in view.
 */
export function BrandBand({ brand, size }: { brand: TenantBrand; size: 'hero' | 'page' | 'bar' }) {
  const name = brand.displayName ?? '';
  const band = bandColors(brand.logoBackground);
  const bandStyle = { backgroundColor: band.bg, color: band.fg };
  const edge = band.light ? ' border-b border-gray-200' : '';
  if (size === 'page') {
    return (
      <header className={'relative ' + (brand.splashUrl ? 'mb-9' : 'mb-2')} data-brand-level="identity">
        <div className={'flex min-h-[136px] items-center justify-center px-6 pb-7 pt-8' + edge} style={bandStyle}>
          {brand.logoOnDarkUrl ? (
            <img
              src={brand.logoOnDarkUrl}
              alt={name}
              className="h-auto max-h-[84px] w-[200px] max-w-full object-contain"
            />
          ) : (
            <span className="text-[26px] font-bold">{name}</span>
          )}
        </div>
        {brand.splashUrl && (
          <img
            src={brand.splashUrl}
            alt=""
            aria-hidden
            className="pointer-events-none absolute inset-x-0 -bottom-6 h-12 w-full select-none object-cover"
          />
        )}
      </header>
    );
  }
  if (size === 'bar') {
    return (
      <header className={'flex h-14 items-center px-5' + edge} style={bandStyle} data-brand-level="action">
        {brand.logoOnDarkUrl ? (
          <img src={brand.logoOnDarkUrl} alt={name} className="h-8 w-auto max-w-[160px] object-contain" />
        ) : (
          <span className="text-[17px] font-bold">{name}</span>
        )}
      </header>
    );
  }
  return (
    <div className={'relative ' + (brand.splashUrl ? 'mb-10' : 'mb-6')} data-brand-level="identity">
      <div className={'flex min-h-[240px] items-center justify-center px-6 pb-10 pt-12' + edge} style={bandStyle}>
        {brand.logoOnDarkUrl ? (
          <img
            src={brand.logoOnDarkUrl}
            alt={name}
            className="h-auto max-h-[120px] w-[240px] max-w-full object-contain"
          />
        ) : (
          <span className="text-3xl font-bold">{name}</span>
        )}
      </div>
      {brand.splashUrl && (
        <img
          src={brand.splashUrl}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-x-0 -bottom-7 h-16 w-full select-none object-cover"
        />
      )}
    </div>
  );
}
